<template>
    <loading v-if="loading"></loading>
    <div class="product-vision-board" v-else>
        <template v-if="isEditing">
            <b-row>
                <div class="col-sm-4">
                    <b-card class="mb-4">
                        <h1 class="mb-3">Product Vision Board</h1>
                        <p>
                            A simple, visual tool to define your product's strategy and guide AI-powered prioritization.
                        </p>
                        <h3 class="mt-4">Benefits</h3>
                        <p>
                            The Product Vision Board helps you clearly define your goals, keep everyone on the same page, focus on what matters, share your ideas easily, and make smarter decisions.
                        </p>
                        <h3 class="mt-4">How to Use</h3>
                        <ol>
                            <li><b>Vision:</b> Define your product's purpose.</li>
                            <li><b>Target Group:</b> Identify your ideal customers.</li>
                            <li><b>Needs:</b> Highlight the problems you solve.</li>
                            <li><b>Product:</b> Describe key features.</li>
                            <li><b>Business Goals:</b> Define success metrics.</li>
                        </ol>
                        <p>
                            Keep it updated as your understanding evolves. Use the form below to create or refine your Product Vision Board!
                        </p>
                    </b-card>
                </div>
                <div class="col-sm-8">
                    <template v-if="!hasExistingVision">
                        <b-card>
                            <p>You don't have a Product Vision Board yet. How would you like to create it?</p>
                            <b-button @click="startAIGeneration" variant="primary" class="mr-2">
                                <b-icon icon="stars"></b-icon> Generate with AI
                            </b-button>
                            <b-button @click="startManualCreation" variant="outline-primary">Create Manually</b-button>
                        </b-card>

                        <b-modal
                            v-model="showAIModal"
                            title="Generate Vision with AI"
                            @ok.prevent="validateAndGenerateVision"
                            cancel-variant="outline"
                        >
                            <validation-observer ref="aiObserver" v-slot="{ validate }">
                                <validation-provider v-slot="{ errors }" name="Product Description" rules="required|max:2000">
                                    <b-form-group label="Describe your product">
                                        <b-form-textarea
                                            v-model="productDescription"
                                            placeholder="Gmail is an email application with solid spam handling."
                                            rows="4"
                                            max-rows="6"
                                        ></b-form-textarea>
                                        <small class="text-muted d-block mt-1 text-right">
                                            {{ productDescription.length }} / 2.000 characters
                                        </small>
                                        <small v-if="errors.length" class="text-danger">{{ errors[0] }}</small>
                                    </b-form-group>
                                </validation-provider>
                            </validation-observer>
                        </b-modal>

                    </template>

                    <template v-else>
                        <validation-observer ref="visionObserver" v-slot="{ validate }">
                            <b-form @submit.prevent="validateAndSaveVision(validate)">
                                <b-alert v-if="successMessage" show variant="success">{{ successMessage }}</b-alert>

                                <b-card class="mb-4">
                                    <validation-provider v-slot="{ errors }" name="Vision" rules="required|max:2000">
                                        <label>Vision</label>
                                        <b-form-textarea
                                            v-model="vision"
                                            placeholder="Enter your product vision"
                                            rows="3"
                                            max-rows="6"
                                        ></b-form-textarea>
                                        <small v-if="errors.length" class="text-danger">{{ errors[0] }}</small>
                                    </validation-provider>
                                </b-card>

                                <b-row>
                                    <b-col md="6" class="mb-4">
                                        <b-card>
                                            <validation-provider v-slot="{ errors }" name="Target Group" rules="required|max:2000">
                                                <label>Target Group</label>
                                                <b-form-textarea
                                                    v-model="target_group"
                                                    placeholder="Who are your target customers?"
                                                    rows="3"
                                                    max-rows="6"
                                                ></b-form-textarea>
                                                <small v-if="errors.length" class="text-danger">{{ errors[0] }}</small>
                                            </validation-provider>
                                        </b-card>
                                    </b-col>
                                    <b-col md="6" class="mb-4">
                                        <b-card>
                                            <validation-provider v-slot="{ errors }" name="Needs" rules="required|max:2000">
                                                <label>Needs</label>
                                                <b-form-textarea
                                                    v-model="needs"
                                                    placeholder="What are their needs?"
                                                    rows="3"
                                                    max-rows="6"
                                                ></b-form-textarea>
                                                <small v-if="errors.length" class="text-danger">{{ errors[0] }}</small>
                                            </validation-provider>
                                        </b-card>
                                    </b-col>
                                    <b-col md="6" class="mb-4">
                                        <b-card>
                                            <validation-provider v-slot="{ errors }" name="Product" rules="required|max:2000">
                                                <label>Product</label>
                                                <b-form-textarea
                                                    v-model="product"
                                                    placeholder="What is the product?"
                                                    rows="3"
                                                    max-rows="6"
                                                ></b-form-textarea>
                                                <small v-if="errors.length" class="text-danger">{{ errors[0] }}</small>
                                            </validation-provider>
                                        </b-card>
                                    </b-col>
                                    <b-col md="6" class="mb-4">
                                        <b-card>
                                            <validation-provider v-slot="{ errors }" name="Business Goals" rules="required|max:2000">
                                                <label>Business Goals</label>
                                                <b-form-textarea
                                                    v-model="business_goals"
                                                    placeholder="What are the business goals?"
                                                    rows="3"
                                                    max-rows="6"
                                                ></b-form-textarea>
                                                <small v-if="errors.length" class="text-danger">{{ errors[0] }}</small>
                                            </validation-provider>
                                        </b-card>
                                    </b-col>
                                </b-row>

                                <div class="text-right">
                                    <b-button type="submit" variant="primary">Save Vision</b-button>
                                </div>
                            </b-form>
                        </validation-observer>
                    </template>
                </div>

            </b-row>
        </template>
        <template v-else>
            <div class="d-flex justify-content-between mb-3">
                <h1>Product Vision Board</h1>
                <div>
                    <b-button
                        @click="handleDelete"
                        :variant="deleteConfirmation ? 'danger' : 'outline'"
                    >
                        <b-icon
                            :icon="deleteConfirmation ? 'exclamation-triangle-fill' : 'trash'"
                            class="me-2"
                        ></b-icon>
                        {{ deleteConfirmation ? 'Sure?' : 'Delete' }}
                    </b-button>
                    <b-button @click="isEditing = true" variant="outline">
                        <b-icon icon="pencil" class="me-2"></b-icon>
                        Edit
                    </b-button>

                </div>
            </div>
            <b-card no-body class="mb-4">
                <b-card-body>
                    <h2 class="mb-3">
                        <b-icon icon="eye" class="mr-2"></b-icon> Vision
                    </h2>
                    <p>{{ vision }}</p>
                </b-card-body>
            </b-card>

            <b-row>
                <b-col md="3" class="mb-4">
                    <b-card no-body class="h-100">
                        <b-card-body>
                            <div class="d-flex justify-content-between">
                                <h2 class="mb-3">Target Group</h2>
                                <h2><b-icon icon="people" class="mr-2"></b-icon></h2>
                            </div>
                            <p>{{ target_group }}</p>
                        </b-card-body>
                    </b-card>
                </b-col>
                <b-col md="3" class="mb-4">
                    <b-card no-body class="h-100">
                        <b-card-body class="h-100">
                            <div class="d-flex justify-content-between">
                                <h2 class="mb-3">Needs</h2>
                                <h2><b-icon icon="heart" class="mr-2"></b-icon></h2>
                            </div>
                            <p>{{ needs }}</p>
                        </b-card-body>
                    </b-card>
                </b-col>
                <b-col md="3" class="mb-4">
                    <b-card no-body class="h-100">
                        <b-card-body>
                            <div class="d-flex justify-content-between">
                                <h2 class="mb-3">Product</h2>
                                <h2><b-icon icon="box-seam" class="mr-2"></b-icon></h2>
                            </div>
                            <p>{{ product }}</p>
                        </b-card-body>
                    </b-card>
                </b-col>
                <b-col md="3" class="mb-4">
                    <b-card no-body class="h-100">
                        <b-card-body>
                            <div class="d-flex justify-content-between">
                                <h2 class="mb-3">Business Goals</h2>
                                <h2><b-icon icon="graph-up" class="mr-2"></b-icon></h2>
                            </div>
                            <p>{{ business_goals }}</p>
                        </b-card-body>
                    </b-card>
                </b-col>
            </b-row>
        </template>
    </div>
</template>

<script>
import NotificationPop
    from "../mixins/NotificationPop";
import Loading
    from "./Loading";

export default {
    name: 'ProductVisionBoard',
    mixins: [NotificationPop],
    components: {
        Loading
    },
    data() {
        return {
            isEditing: true,
            hasExistingVision: false,
            showAIModal: false,
            productDescription: '',
            vision: '',
            target_group: '',
            needs: '',
            product: '',
            business_goals: '',
            loading: true,
            successMessage: null,
            deleteConfirmation: false,
        }
    },
    created() {
        this.fetchVision()
    },
    methods: {
        startAIGeneration() {
            this.showAIModal = true
        },
        startManualCreation() {
            this.isEditing = true
            this.hasExistingVision = true
        },
        validateAndGenerateVision() {
            this.$refs.aiObserver.validate().then((isValid) => {
                if (isValid) {
                    // Close the modal and proceed
                    this.showAIModal = false;
                    this.generateVision(); // Your actual function to generate the vision
                }
            });
        },
        validateAndSaveVision() {
            this.$refs.visionObserver.validate().then((isValid) => {
                if (isValid) {
                    this.saveVision(); // Your actual function to generate the vision
                }
            });
        },
        generateVision() {
            this.loading = true
            this.$http.post('/http_api/product-vision/generate', { description: this.productDescription })
                .then(response => {
                    if (response.data.status === 'no_credits') {
                        this.popNotification('Not enough AI credits to generate vision.', 'warning')
                    } else {
                        this.vision = response.data.vision
                        this.target_group = response.data.target_group
                        this.needs = response.data.needs
                        this.product = response.data.product
                        this.business_goals = response.data.business_goals
                        this.isEditing = false
                        this.hasExistingVision = true
                        this.successMessage = 'Great! Your product vision is generated.'
                        this.popNotification('Vision generated successfully.', 'success')
                    }
                })
                .catch(error => {
                    this.popNotification('Failed to generate vision using input: '+this.productDescription, 'danger')
                    console.error('Error generating vision:', error)
                })
                .finally(() => {
                    this.loading = false
                    this.showAIModal = false
                })
        },
        fetchVision() {
            this.$http.get('/http_api/product-vision')
                .then(response => {
                    const data = response.data
                    console.log(response.data)
                    this.vision = data.vision
                    this.target_group = data.target_group
                    this.needs = data.needs
                    this.product = data.product
                    this.business_goals = data.business_goals
                    this.isEditing = false
                    this.loading = false
                    this.hasExistingVision = true
                })
                .catch(error => {
                    if (error.response && error.response.status === 404) {
                        // Vision not found, set to editing mode
                        this.isEditing = true
                        this.loading = false
                        this.popNotification('No existing vision found. You can create one now.', 'info')
                    } else {
                        // Other error occurred
                        this.popNotification('Failed to fetch product vision.', 'danger')
                        console.error('Error fetching product vision:', error)
                        this.isEditing = false  // Keep in view mode if there's an error other than 404
                    }
                })
        },
        saveVision() {
            const visionData = {
                vision: this.vision,
                target_group: this.target_group,
                needs: this.needs,
                product: this.product,
                business_goals: this.business_goals
            }

            this.$http.post('/http_api/product-vision', visionData)
                .then(response => {
                    this.popNotification('Product vision saved successfully.', 'success')
                    this.isEditing = false
                })
                .catch(error => {
                    this.popNotification('Failed to save product vision.', 'danger')
                    console.error('Error saving product vision:', error)
                })
        },
        handleDelete() {
            if (this.deleteConfirmation) {
                this.deleteVision();
            } else {
                this.deleteConfirmation = true;
                // Reset confirmation after 3 seconds if not clicked
                setTimeout(() => {
                    this.deleteConfirmation = false;
                }, 3000);
            }
        },

        deleteVision() {
            this.loading = true;
            this.$http.delete('/http_api/product-vision')
                .then(response => {
                    this.popNotification('Product Vision Board deleted successfully.', 'success');
                    this.resetVisionData();
                    this.isEditing = true;
                    this.hasExistingVision = false;
                })
                .catch(error => {
                    this.popNotification('Failed to delete Product Vision Board.', 'danger');
                    console.error('Error deleting Product Vision Board:', error);
                })
                .finally(() => {
                    this.loading = false;
                    this.deleteConfirmation = false;
                });
        },

        resetVisionData() {
            this.vision = '';
            this.target_group = '';
            this.needs = '';
            this.product = '';
            this.business_goals = '';
        },
    }
}
</script>

<style scoped>
.product-vision-board {
    padding-top: 2rem;
    padding-bottom: 2rem;
}

h1 {
    margin-bottom: 2rem;
}

h2 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
}
</style>